// src/components/NewJobView.tsx
import React from 'react';
import { Scissors, Plus, Calendar, Clock } from 'lucide-react';
import { formatPrice } from '../utils/formatters';

interface NewJobViewProps {
// Data & States uit App.tsx
customers: any[];
stringers: any[];
inventory: any[];
newJob: any;
setNewJob: (val: any) => void;
// Klanten dropdown states
customerSearchTerm: string;
setCustomerSearchTerm: (val: string) => void;
showCustomerDropdown: boolean;
setShowCustomerDropdown: (val: boolean) => void;
// Racket dropdown states
showRacketDropdown: boolean;
setShowRacketDropdown: (val: boolean) => void;
customerPreviousRackets: string[];
setCustomerPreviousRackets: (val: string[]) => void;
// Snaren dropdown states
stringSearchTerm: string;
showStringDropdown: boolean;
setShowStringDropdown: (val: boolean) => void;
crossStringSearchTerm: string;
showCrossStringDropdown: boolean;
setShowCrossStringDropdown: (val: boolean) => void;

// Handlers & Berekeningen
addJob: (e: React.FormEvent) => void;
createQuickCustomer: (name: string) => Promise<string | null>;
selectCustomerForJob: (customer: any) => void;
findLastTensionForRacket: (customerId: string, racket: string) => any;
toggleHybridMode: () => void;
handleManualStringInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
selectStringForJob: (item: any) => void;
handleManualCrossStringInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
selectCrossStringForJob: (item: any) => void;
handleBaseGripChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
handleOverGripChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
calculateNewPrice: (updates: any) => number | string;
}

export default function NewJobView({
customers, stringers, inventory, newJob, setNewJob,
customerSearchTerm, setCustomerSearchTerm, showCustomerDropdown, setShowCustomerDropdown,
showRacketDropdown, setShowRacketDropdown, customerPreviousRackets, setCustomerPreviousRackets,
stringSearchTerm, showStringDropdown, setShowStringDropdown,
crossStringSearchTerm, showCrossStringDropdown, setShowCrossStringDropdown,
addJob, createQuickCustomer, selectCustomerForJob, findLastTensionForRacket,
toggleHybridMode, handleManualStringInput, selectStringForJob,
handleManualCrossStringInput, selectCrossStringForJob,
handleBaseGripChange, handleOverGripChange, calculateNewPrice
}: NewJobViewProps) {
const exactMatch = customers.some(c => c.name.toLowerCase() === customerSearchTerm.toLowerCase().trim());
const filteredCustomers = customers.filter(c => c.name.toLowerCase().includes(customerSearchTerm.toLowerCase()));

return (
<div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
<div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
<h2 className="text-xl font-bold text-slate-800 dark:text-white mb-6 flex items-center"><Scissors className="w-5 h-5 mr-2 text-indigo-600" />Nieuwe Bespanning</h2>
<form onSubmit={addJob} className="space-y-6">
{stringers.length > 0 && (
<div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800">
<label className="block text-sm font-bold text-indigo-900 dark:text-indigo-200 mb-2">Bespanner</label>
<select
className="w-full p-3 rounded-xl bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-700 text-slate-900 dark:text-white"
value={newJob.stringer}
onChange={(e) => setNewJob({...newJob, stringer: e.target.value})}
>
<option value="">-- Kies bespanner --</option>
{stringers.map(s => (
<option key={s.id} value={s.name}>{s.name}</option>
))}
</select>
</div>
)}

<div className="relative">
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Selecteer Klant</label>
<input type="text" placeholder="Zoek..." className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white" value={customerSearchTerm} onChange={(e) => { setCustomerSearchTerm(e.target.value); setShowCustomerDropdown(true); setNewJob({...newJob, customerId: ''}); }} onFocus={() => setShowCustomerDropdown(true)} onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)} />
{showCustomerDropdown && customerSearchTerm && (
<ul className="absolute z-10 w-full bg-white dark:bg-slate-800 mt-1 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700 max-h-60 overflow-y-auto">
{!exactMatch && customerSearchTerm.trim() !== '' && (
<li
onClick={async () => {
const id = await createQuickCustomer(customerSearchTerm);
if(id) {
setNewJob({ ...newJob, customerId: id });
setCustomerSearchTerm(customerSearchTerm.trim());
setShowCustomerDropdown(false);
setCustomerPreviousRackets([]);
}
}}
className="px-4 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/50 cursor-pointer flex justify-between items-center border-b border-slate-50 dark:border-slate-700 text-indigo-600 dark:text-indigo-400 font-bold"
>
<div className="flex items-center">
<Plus className="w-5 h-5 mr-3" />
<span>Voeg "{customerSearchTerm}" toe als nieuwe klant</span>
</div>
</li>
)}

{filteredCustomers.map(c => (
<li key={c.id} onMouseDown={() => selectCustomerForJob(c)} className="px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer flex justify-between items-center border-b border-slate-50 dark:border-slate-700 last:border-0">
<div className="flex items-center">
<div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-300 flex items-center justify-center font-bold text-xs mr-3">{c.name.charAt(0)}</div>
<div><p className="text-sm font-medium text-slate-800 dark:text-white">{c.name}</p></div>
</div>
</li>
))}
</ul>
)}
</div>
<div className="relative">
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Racket</label>
<input
type="text"
className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white"
placeholder="Merk & Model (bv. Babolat Pure Aero)"
value={newJob.racket}
onChange={(e) => setNewJob({...newJob, racket: e.target.value})}
onFocus={() => setShowRacketDropdown(true)}
onBlur={() => setTimeout(() => setShowRacketDropdown(false), 200)}
required
/>
{showRacketDropdown && customerPreviousRackets.length > 0 && (
<ul className="absolute z-10 w-full bg-white dark:bg-slate-800 mt-1 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700 max-h-48 overflow-y-auto">
<li className="px-4 py-2 text-xs font-bold text-slate-400 bg-slate-50 dark:bg-slate-900/50">Eerder bespannen voor deze klant:</li>
{customerPreviousRackets.map((racket, idx) => (
<li
key={idx}
onMouseDown={() => {
const found = findLastTensionForRacket(newJob.customerId, racket);
setNewJob({
...newJob,
racket: racket,
...(found ? found : {})
});
setShowRacketDropdown(false);
}}
className="px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer text-sm text-slate-700 dark:text-slate-200 border-b border-slate-50 dark:border-slate-700"
>
{racket} {findLastTensionForRacket(newJob.customerId, racket) && <span className="text-xs text-indigo-500 ml-2">({findLastTensionForRacket(newJob.customerId, racket).tensionMain}/{findLastTensionForRacket(newJob.customerId, racket).tensionCross})</span>}
</li>
))}
</ul>
)}
</div>
<div className="bg-slate-50 dark:bg-slate-900/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Snaar (Lengte)</label>
<div className="flex items-center mb-3">
<input
type="checkbox"
id="hybridToggle"
checked={newJob.isHybrid}
onChange={toggleHybridMode}
className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 border-gray-300 dark:border-slate-600 dark:bg-slate-700"
/>
<label htmlFor="hybridToggle" className="ml-2 text-sm text-slate-600 dark:text-slate-400 font-bold cursor-pointer">Hybrid (2 verschillende snaren)</label>
</div>

<div className="relative mb-3">
<input
type="text"
className="w-full p-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white"
placeholder={newJob.isHybrid ? "Zoek lengte snaar..." : "Zoek snaar of typ eigen..."}
value={stringSearchTerm}
onChange={handleManualStringInput}
onFocus={() => setShowStringDropdown(true)}
onBlur={() => setTimeout(() => setShowStringDropdown(false), 200)}
/>
{showStringDropdown && stringSearchTerm && (
<ul className="absolute z-10 w-full bg-white dark:bg-slate-800 mt-1 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700 max-h-48 overflow-y-auto">
<li
onMouseDown={() => {
const updates = { stringId: 'custom', customString: stringSearchTerm };
setNewJob((prev: any) => ({ ...prev, ...updates, price: calculateNewPrice(updates) }));
setShowStringDropdown(false);
}}
className="px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer text-sm font-bold text-indigo-600 dark:text-indigo-400 border-b border-slate-50 dark:border-slate-700"
>
Gebruik "{stringSearchTerm}" als handmatige snaar
</li>
{inventory
.filter(i => ['Basisgrip', 'Overgrip', 'Demper'].indexOf(i.category) === -1)
.filter(i => `${i.brand} ${i.model}`.toLowerCase().indexOf(stringSearchTerm.toLowerCase()) !== -1)
.sort((a, b) => {
const stockA = parseInt(a.stock) || 0;
const stockB = parseInt(b.stock) || 0;
if (stockA > 0 && stockB <= 0) return -1;
if (stockA <= 0 && stockB > 0) return 1;
return a.brand.localeCompare(b.brand);
})
.map(item => (
<li
key={item.id}
onMouseDown={() => selectStringForJob(item)}
className="px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer text-sm text-slate-700 dark:text-slate-200 flex justify-between"
>
<span className={parseInt(item.stock) <= 0 ? "text-slate-400 dark:text-slate-500" : ""}>
{item.brand} {item.model}
{parseInt(item.stock) <= 0 && " (Geen voorraad)"}
</span>
<span className="font-bold">€ {formatPrice(item.price)}</span>
</li>
))
}
</ul>
)}
</div>

{newJob.isHybrid && (
<div className="relative mb-3 animate-in fade-in slide-in-from-top-2 duration-300">
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Breedte Snaar (Cross)</label>
<input
type="text"
className="w-full p-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white"
placeholder="Zoek breedte snaar..."
value={crossStringSearchTerm}
onChange={handleManualCrossStringInput}
onFocus={() => setShowCrossStringDropdown(true)}
onBlur={() => setTimeout(() => setShowCrossStringDropdown(false), 200)}
/>
{showCrossStringDropdown && crossStringSearchTerm && (
<ul className="absolute z-10 w-full bg-white dark:bg-slate-800 mt-1 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700 max-h-48 overflow-y-auto">
<li
onMouseDown={() => {
const updates = { crossStringId: 'custom', customCrossString: crossStringSearchTerm };
setNewJob((prev: any) => ({ ...prev, ...updates, price: calculateNewPrice(updates) }));
setShowCrossStringDropdown(false);
}}
className="px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer text-sm font-bold text-indigo-600 dark:text-indigo-400 border-b border-slate-50 dark:border-slate-700"
>
Gebruik "{crossStringSearchTerm}" als handmatige breedte snaar
</li>
{inventory
.filter(i => ['Basisgrip', 'Overgrip', 'Demper'].indexOf(i.category) === -1)
.filter(i => `${i.brand} ${i.model}`.toLowerCase().indexOf(crossStringSearchTerm.toLowerCase()) !== -1)
.sort((a, b) => {
const stockA = parseInt(a.stock) || 0;
const stockB = parseInt(b.stock) || 0;
if (stockA > 0 && stockB <= 0) return -1;
if (stockA <= 0 && stockB > 0) return 1;
return a.brand.localeCompare(b.brand);
})
.map(item => (
<li
key={item.id}
onMouseDown={() => selectCrossStringForJob(item)}
className="px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer text-sm text-slate-700 dark:text-slate-200 flex justify-between"
>
<span className={parseInt(item.stock) <= 0 ? "text-slate-400 dark:text-slate-500" : ""}>
{item.brand} {item.model}
{parseInt(item.stock) <= 0 && " (Geen voorraad)"}
</span>
<span className="font-bold">€ {formatPrice(item.price)}</span>
</li>
))
}
</ul>
)}
</div>
)}

<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 pt-2 border-t border-slate-100 dark:border-slate-700">Kies Basisgrip</label>
<select className="w-full p-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 mb-2 selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white" value={newJob.baseGripId || ''} onChange={handleBaseGripChange}>
<option value="">-- Geen --</option>
{inventory.filter(i => i.category === 'Basisgrip').map(item => (<option key={item.id} value={item.id}>{item.brand} {item.model} - €{formatPrice(item.price)}</option>))}
</select>
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 pt-2 border-t border-slate-100 dark:border-slate-700">Kies Overgrip</label>
<select className="w-full p-3 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 focus:ring-2 focus:ring-indigo-500 mb-1 selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white" value={newJob.overGripId || ''} onChange={handleOverGripChange}>
<option value="">-- Geen --</option>
{inventory.filter(i => i.category === 'Overgrip').map(item => (<option key={item.id} value={item.id}>{item.brand} {item.model} - €{formatPrice(item.price)}</option>))}
</select>
</div>
<div className="grid grid-cols-12 gap-4">
<div className="col-span-4">
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Lengte</label>
<input type="number" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white" value={newJob.tensionMain} onChange={(e) => setNewJob({...newJob, tensionMain: e.target.value})} />
</div>
<div className="col-span-4">
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Breedte</label>
<input type="number" className="w-full p-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white" value={newJob.tensionCross} onChange={(e) => setNewJob({...newJob, tensionCross: e.target.value})} />
</div>
<div className="col-span-4">
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Eenheid</label>
<div className="flex bg-slate-100 dark:bg-slate-900 rounded-xl p-1">
<button
type="button"
onClick={() => setNewJob({...newJob, tensionUnit: 'kg'})}
className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${newJob.tensionUnit === 'kg' ? 'bg-white dark:bg-slate-700 shadow text-indigo-600 dark:text-white' : 'text-slate-400'}`}
>KG</button>
<button
type="button"
onClick={() => setNewJob({...newJob, tensionUnit: 'lbs'})}
className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${newJob.tensionUnit === 'lbs' ? 'bg-white dark:bg-slate-700 shadow text-indigo-600 dark:text-white' : 'text-slate-400'}`}
>LBS</button>
</div>
</div>
</div>
<div>
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Prijs (Berekend obv geselecteerde materialen)</label>
<input type="number" step="0.50" className={`w-full p-3 rounded-xl border-none font-bold selection:bg-lime-300 selection:text-slate-900 ${newJob.stringId !== 'custom' || newJob.baseGripId || newJob.overGripId ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-white'}`} value={newJob.price} onChange={(e) => setNewJob({...newJob, price: e.target.value})} />
</div>
<div>
<label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Deadline / Gereed op</label>
<div className="flex gap-2">
<div className="relative flex-1">
<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Calendar className="h-5 w-5 text-slate-400" /></div>
<input type="date" className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white" value={newJob.deadline} onChange={(e) => setNewJob({...newJob, deadline: e.target.value})}/>
</div>
<div className="relative w-32">
<div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><Clock className="h-5 w-5 text-slate-400" /></div>
<input type="time" className="w-full pl-10 pr-4 py-3 rounded-xl bg-slate-50 dark:bg-slate-900 border-none focus:ring-2 focus:ring-indigo-500 selection:bg-lime-300 selection:text-slate-900 text-slate-900 dark:text-white" value={newJob.deadlineTime} onChange={(e) => setNewJob({...newJob, deadlineTime: e.target.value})}/>
</div>
</div>
</div>
<button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-4 rounded-xl font-bold transition">Opslaan</button>
</form>
</div>
</div>
);
} 
